import { ref } from 'vue'
import type { AdminUser, AdminUserDetail, UserSort, UserStatusFilter } from '@/types/users'
import type { PaginationMeta } from '@/types/shared'
import { listUsers, fetchUser, verifyUserEmail, deactivateUser } from '@/api/users'

export function useUsers() {
  const items = ref<AdminUser[]>([])
  const meta = ref<PaginationMeta | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)
  const search = ref('')
  const status = ref<UserStatusFilter | undefined>(undefined)
  const sort = ref<UserSort>('created_at_desc')
  const page = ref(1)
  const detail = ref<AdminUserDetail | null>(null)
  const actionLoading = ref(false)
  const actionError = ref<string | null>(null)

  async function fetchList(): Promise<void> {
    loading.value = true
    error.value = null
    try {
      const response = await listUsers({
        search: search.value || undefined,
        status: status.value,
        sort: sort.value,
        page: page.value,
        per_page: 25,
      })
      items.value = response.data
      meta.value = response.meta
    } catch {
      error.value = 'admin.common.error_loading'
    } finally {
      loading.value = false
    }
  }

  async function fetchDetail(id: number): Promise<void> {
    loading.value = true
    error.value = null
    try {
      detail.value = await fetchUser(id)
    } catch (e: unknown) {
      if (isNotFoundError(e)) {
        error.value = 'admin.common.not_found'
      } else {
        error.value = 'admin.common.error_loading'
      }
    } finally {
      loading.value = false
    }
  }

  async function verifyEmail(id: number): Promise<boolean> {
    actionLoading.value = true
    actionError.value = null
    try {
      detail.value = await verifyUserEmail(id)
      return true
    } catch (e: unknown) {
      if (is422Error(e)) {
        const msg = extractFirstError(e)
        actionError.value = msg ?? 'admin.users.already_verified'
      } else {
        actionError.value = 'admin.common.error_generic'
      }
      return false
    } finally {
      actionLoading.value = false
    }
  }

  async function deactivate(id: number): Promise<boolean> {
    actionLoading.value = true
    actionError.value = null
    try {
      detail.value = await deactivateUser(id)
      return true
    } catch (e: unknown) {
      if (is422Error(e)) {
        const msg = extractFirstError(e)
        actionError.value = msg ?? 'admin.users.already_deactivated'
      } else {
        actionError.value = 'admin.common.error_generic'
      }
      return false
    } finally {
      actionLoading.value = false
    }
  }

  return {
    items,
    meta,
    loading,
    error,
    search,
    status,
    sort,
    page,
    detail,
    actionLoading,
    actionError,
    fetchList,
    fetchDetail,
    verifyEmail,
    deactivate,
  }
}

function isNotFoundError(e: unknown): boolean {
  return (
    typeof e === 'object' &&
    e !== null &&
    'response' in e &&
    typeof (e as { response: unknown }).response === 'object' &&
    (e as { response: { status: number } }).response.status === 404
  )
}

function is422Error(e: unknown): boolean {
  return (
    typeof e === 'object' &&
    e !== null &&
    'response' in e &&
    typeof (e as { response: unknown }).response === 'object' &&
    (e as { response: { status: number } }).response.status === 422
  )
}

function extractFirstError(e: unknown): string | null {
  if (
    typeof e === 'object' &&
    e !== null &&
    'response' in e &&
    typeof (e as { response: unknown }).response === 'object'
  ) {
    const resp = (e as { response: { data?: { errors?: Record<string, string[]> } } }).response
    const errors = resp.data?.errors
    if (errors) {
      const firstKey = Object.keys(errors)[0]
      if (firstKey && errors[firstKey]?.[0]) {
        return errors[firstKey][0]
      }
    }
  }
  return null
}
